import {forEach} from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {Map} from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import FixedTooltip from 'generic/FixedTooltip/FixedTooltip.react';

import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {getDifficultySpan} from 'lib/questionRenderUtils';
import * as ClassroomUtil from 'client/Classrooms/ClassroomUtil';

import * as TableUtil from './StudentInsightUtil';

/*
  TODO:
    - Move these two tables into separate components.
*/
export default class StudentInsightTable extends React.Component {
  static propTypes = {
    studentInsights: ImmutablePropTypes.map,
    attemptNumber: PropTypes.number,
    showGuess: PropTypes.bool,
    /*
      These transform flags are specific to this table's usage in the
      Teacher assignments drilldown. They are here to do some data massaging
      so we are able to reuse this  code.
    */
    transformToCamel: PropTypes.bool,
    transformToOneAttempt: PropTypes.bool
  };

  constructor(props) {
    super(props);
    this.nodeRefs = {};
  }

  componentDidMount() {
    this.sizeThePseudoheader();
  }

  componentDidUpdate() {
    this.sizeThePseudoheader();
  }

  getTooltipContentForData(tooltipData) {
    return (
      <div className='insight-table__question-answer-badge-tooltip'>
        <div className='insight-table__question-answer-badge-tooltip-datum'>
          <span className='insight-table__question-answer-badge-tooltip-term'>
            {TableUtil.getAttemptLanguage(tooltipData.get('attemptNumber'))}
          </span>
        </div>
        <div className='insight-table__question-answer-badge-tooltip-datum'>
          <dt className='insight-table__question-answer-badge-tooltip-term'>Duration</dt>
          <dd className='insight-table__question-answer-badge-tooltip-definition'>
            {tooltipData.get('duration')}
          </dd>
        </div>
        <div className='insight-table__question-answer-badge-tooltip-datum'>
          <dt className='insight-table__question-answer-badge-tooltip-term'>Timestamp</dt>
          <dd className='insight-table__question-answer-badge-tooltip-definition'>
            {tooltipData.get('timestamp')}
          </dd>
        </div>
      </div>
    );
  }

  getAnswerBadge(dataMap) {
    const isAnswered = dataMap.get('isAnswered');
    const isCorrect = dataMap.get('isCorrect');
    const tooltipData = dataMap.get('fullAttemptData');
    const key = dataMap.get('key');

    if (isAnswered === false) {
      return <div key={key} className='fa fa-minus question-answer-badge unanswered' />;
    }

    /*
      If we have an answered question, lets set up our classes
      based on its correctness
    */
    const answeredClass = isCorrect ? 'correct' : 'incorrect';
    const symbolClass = isCorrect ? 'fa-check' : 'fa-times';

    if (tooltipData === undefined) {
      return (
        <div key={key} className={`fa ${symbolClass} question-answer-badge ${answeredClass}`} />
      );
    }

    if (tooltipData.get('isAnswered') === false) {
      return (
        <div key={key} className={`fa ${symbolClass} question-answer-badge ${answeredClass}`} />
      );
    }

    return (
      <div key={key} className={`fa ${symbolClass} question-answer-badge ${answeredClass}`}>
        <FixedTooltip>{this.getTooltipContentForData(tooltipData)}</FixedTooltip>
      </div>
    );
  }

  sizeThePseudoheader() {
    forEach(this.nodeRefs.pseudoHeader.children, (node, i) => {
      node.style.width = `${this.nodeRefs.realHeader.children[i].clientWidth}px`;
    });
  }

  /*
    Returns a table limited by attempt
  */
  getAttemptTable() {
    let tableHeadings;
    if (!this.props.showGuess) {
      tableHeadings = ['Question', 'Duration', 'Made At', 'Result'];
    } else {
      tableHeadings = ['Question', 'Duration', 'Guess', 'Made At', 'Result'];
    }

    return (
      <div>
        <table className='insight-table overflow-table__heading'>
          <thead>
            <tr
              ref={(node) => {
                this.nodeRefs.pseudoHeader = node;
              }}
            >
              {tableHeadings.map((heading, key) => {
                return <th key={key}>{heading}</th>;
              })}
            </tr>
          </thead>
        </table>
        <div className='overflow-table__overflow'>
          <table className='insight-table'>
            <thead>
              <tr
                ref={(node) => {
                  this.nodeRefs.realHeader = node;
                }}
              >
                {tableHeadings.map((heading, key) => {
                  return <th key={key}>{heading}</th>;
                })}
              </tr>
            </thead>

            <tbody>
              {this.props.studentInsights
                .map((questionInsights) => {
                  /*
                  Here we show all insight data, but we grey out questions that do not have an
                  answer, or have not been answered for the selected attempt number.
                */

                  // TODO: This is a temporary thing until the mandark
                  // response returns camelcased values
                  if (this.props.transformToCamel) {
                    questionInsights = TableUtil.transformToCamel(questionInsights);
                  }

                  /*
                  This is a hack to account for the case of a student having already made a guess
                  for a question outside of an assignment. The assumption we originally had at this point in the code
                  was that for guess insights against an assignment, we would always only have one guess come back.

                  This is still true, but what we didn't figure was that the attempt number for that question could be
                  a different value from `1`.

                  We are just normalizing to one in that case.

                  This is not going to happen for the normal insights view. Only the assignment guess insights view.
                */
                  if (this.props.transformToOneAttempt) {
                    questionInsights = questionInsights.map((insightObject) => {
                      const questionAttemptNumber = insightObject.get('questionAttemptNumber');
                      if (questionAttemptNumber) {
                        return insightObject.set('questionAttemptNumber', 1);
                      }
                      return insightObject;
                    });
                  }

                  const attemptString = TableUtil.getAttemptTimestampStringForStudentInsightEntry(
                    questionInsights,
                    this.props.attemptNumber
                  );
                  const hasQuestionBeenAnswered =
                    TableUtil.isAnsweredForAttemptInStudentInsightEntry(
                      questionInsights,
                      this.props.attemptNumber
                    );
                  const isCorrect = TableUtil.isCorrectForAttemptInStudentInsightEntry(
                    questionInsights,
                    this.props.attemptNumber
                  );

                  const questionName = questionInsights.first().get('questionName');
                  const questionSlug = questionInsights.first().get('questionSlug');
                  const questionId = questionInsights.first().get('questionId');
                  const questionSetSlug = questionInsights.first().get('questionSetSlug');
                  const duration = TableUtil.getDurationForAttemptInStudentInsightEntry(
                    questionInsights,
                    this.props.attemptNumber
                  );
                  const questionDifficulty = TableUtil.getDifficultyForQuestion(questionInsights);
                  const answerForAttempt = TableUtil.getGuessValueForAttempt(
                    questionInsights,
                    this.props.attemptNumber
                  );

                  let guessTableData;
                  if (this.props.showGuess) {
                    guessTableData = <td>{answerForAttempt.join(', ')}</td>;
                  } else {
                    guessTableData = null;
                  }
                  return (
                    <tr
                      key={questionId}
                      className={classnames({
                        'insight-table__row-disabled': hasQuestionBeenAnswered === false
                      })}
                    >
                      <td
                        className='insight-table__question-info'
                        onClick={ClassroomUtil.makeNavigateToQuestion(
                          questionSetSlug,
                          questionSlug
                        )}
                      >
                        <div className='insight-table__question-info-difficulty'>
                          {getDifficultySpan(questionDifficulty, !hasQuestionBeenAnswered)}
                        </div>
                        <div className='insight-table__question-info-name'>
                          <MarkdownRendererV2 text={questionName} />
                        </div>
                      </td>
                      <td>{duration}</td>
                      {guessTableData}
                      <td>{attemptString}</td>
                      <td>
                        {this.getAnswerBadge(
                          new Map({
                            isAnswered: hasQuestionBeenAnswered,
                            isCorrect,
                            fullAttemptData: undefined,
                            key: questionId
                          })
                        )}
                      </td>
                    </tr>
                  );
                })
                .toList()}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /*
    retrieves a table not filtered by attempt
  */
  getTable() {
    let tableHeadings;
    if (!this.props.showGuess) {
      tableHeadings = ['Question', 'Most Recent Attempt', 'Result'];
    } else {
      tableHeadings = ['Question', 'Most Recent Guess', 'Most Recent Attempt', 'Result'];
    }
    return (
      <div>
        <table className='insight-table overflow-table__heading'>
          <thead>
            <tr ref={(node) => (this.nodeRefs.pseudoHeader = node)}>
              {tableHeadings.map((heading, key) => {
                return <th key={key}>{heading}</th>;
              })}
            </tr>
          </thead>
        </table>
        <div className='overflow-table__overflow' ref={(node) => (this.nodeRefs.overflow = node)}>
          <table className='insight-table'>
            <thead>
              <tr ref={(node) => (this.nodeRefs.realHeader = node)}>
                {tableHeadings.map((heading, key) => {
                  return <th key={key}>{heading}</th>;
                })}
              </tr>
            </thead>

            <tbody>
              {this.props.studentInsights
                .map((questionInsights) => {
                  // TODO: This is a temporary thing until the mandark
                  // response returns camelcased values
                  if (this.props.transformToCamel) {
                    questionInsights = TableUtil.transformToCamel(questionInsights);
                  }

                  /*
                  See comment in `getAttemptTable` for an explanation of what this is doing.
                */
                  if (this.props.transformToOneAttempt) {
                    questionInsights = questionInsights.map((insightObject) => {
                      const questionAttemptNumber = insightObject.get('questionAttemptNumber');
                      if (questionAttemptNumber) {
                        return insightObject.set('questionAttemptNumber', 1);
                      }
                      return insightObject;
                    });
                  }

                  const mostRecentTimestampString =
                    TableUtil.getRecentAttemptTimestampString(questionInsights);
                  const questionName = questionInsights.first().get('questionName');
                  const questionSlug = questionInsights.first().get('questionSlug');
                  const questionId = questionInsights.first().get('questionId');
                  const questionSetSlug = questionInsights.first().get('questionSetSlug');
                  const questionDifficulty = TableUtil.getDifficultyForQuestion(questionInsights);
                  const attemptList = TableUtil.getAttemptList(questionInsights);
                  const hasQuestionBeenAnswered =
                    TableUtil.isAnsweredInStudentInsightEntry(questionInsights);
                  const mostRecentGuess = TableUtil.getMostRecentGuessValue(questionInsights);

                  let guessTableData;
                  if (this.props.showGuess) {
                    guessTableData = (
                      <td className='insight-table__question-guess'>
                        {mostRecentGuess.join(', ')}
                      </td>
                    );
                  } else {
                    guessTableData = null;
                  }
                  return (
                    <tr
                      key={questionId}
                      className={classnames({
                        'insight-table__row-disabled': hasQuestionBeenAnswered === false
                      })}
                    >
                      <td
                        className='insight-table__question-info'
                        onClick={ClassroomUtil.makeNavigateToQuestion(
                          questionSetSlug,
                          questionSlug
                        )}
                      >
                        <div className='insight-table__question-info-difficulty'>
                          {getDifficultySpan(questionDifficulty, !hasQuestionBeenAnswered)}
                        </div>
                        <div className='insight-table__question-info-name'>
                          <MarkdownRendererV2 text={questionName} />
                        </div>
                      </td>
                      {guessTableData}
                      <td>{mostRecentTimestampString}</td>
                      <td className='insight-table__question-answer-badge-list'>
                        {attemptList.map((attemptData, key) => {
                          return this.getAnswerBadge(
                            new Map({
                              isAnswered: attemptData.get('isAnswered'),
                              isCorrect: attemptData.get('isCorrect'),
                              fullAttemptData: attemptData,
                              key
                            })
                          );
                        })}
                      </td>
                    </tr>
                  );
                })
                .toList()}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    if (this.props.attemptNumber) {
      return this.getAttemptTable();
    }

    return this.getTable();
  }
}
